import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { PlusIcon, SmartphoneIcon } from 'lucide-react'
import {
	AlertBanner,
	Badge,
	Button,
	EmptyState,
	Spinner,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@rentflowdev/ui'
import { useListBuildProfilesQuery } from '@/api'
import type { BuildProfile } from '@rentflowdev/api'
import { DEFAULT_LOCALE } from '@/types'
import { extractErrorMessage } from '@/lib/error-message'
import { BuilderProfileDialog } from '@/components/builder-profile-dialog'
import { useCan } from '@/lib/access'

export const Route = createFileRoute('/_app/builder/')({
	component: BuilderProfilesPage,
})

function formatDate(iso: string): string {
	return new Date(iso).toLocaleDateString(DEFAULT_LOCALE, {
		day: '2-digit', month: '2-digit', year: 'numeric',
	})
}

function BuilderProfilesPage() {
	const canCreate = useCan('create', 'BuildProfile')
	const navigate = useNavigate()
	const [createOpen, setCreateOpen] = useState(false)
	const { data, isLoading, error } = useListBuildProfilesQuery({ includeInactive: true })

	const profiles = data?.profiles ?? []

	return (
		<>
			<BuilderProfileDialog open={createOpen} onOpenChange={setCreateOpen} />

			<div className="flex items-center justify-between px-4 py-4">
				<h1 className="text-2xl font-[500] tracking-[-0.6px] text-(--color-foreground)">
					Мобильные приложения
				</h1>
				{canCreate && (
					<Button iconLeft={<PlusIcon className="size-4" />} onClick={() => setCreateOpen(true)}>
						Новое приложение
					</Button>
				)}
			</div>

			{isLoading && (
				<div className="flex flex-1 items-center justify-center">
					<Spinner />
				</div>
			)}

			{!isLoading && error !== undefined && (
				<div className="px-4">
					<AlertBanner
						variant="destructive"
						title="Не удалось загрузить профили"
						description={extractErrorMessage(error)}
					/>
				</div>
			)}

			{!isLoading && error === undefined && profiles.length === 0 && (
				<EmptyState
					illustration={<SmartphoneIcon className="size-12 text-(--color-muted-foreground)" />}
					title="Пока нет ни одного приложения"
					description="Создайте профиль своего приложения, чтобы запустить первую сборку"
					action={{ label: 'Новое приложение', onClick: () => setCreateOpen(true) }}
				/>
			)}

			{!isLoading && error === undefined && profiles.length > 0 && (
				<div className="flex-1 overflow-auto px-4 pb-4">
					<div className="rounded-lg border border-(--color-border) overflow-hidden">
						<Table>
							<TableHeader>
								<TableRow className="hover:bg-transparent">
									<TableHead className="text-(--color-muted-foreground) font-[500]">Название приложения</TableHead>
									<TableHead className="text-(--color-muted-foreground) font-[500]">Версия</TableHead>
									<TableHead className="text-(--color-muted-foreground) font-[500]">Статус</TableHead>
									<TableHead className="text-(--color-muted-foreground) font-[500]">Обновлён</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{profiles.map((profile: BuildProfile) => (
									<TableRow
										key={profile.id}
										className="h-16 cursor-pointer"
										onClick={() =>
											void navigate({
												to: '/builder/$profileId',
												params: { profileId: profile.id },
											})
										}
									>
										<TableCell className="px-4">
											<div className="flex min-w-0 items-center gap-2">
												{profile.branding?.logo?.startsWith('https://') && (
													<img
														src={profile.branding.logo}
														alt=""
														className="size-6 shrink-0 rounded-md border border-(--color-border) object-cover"
													/>
												)}
												<span className="font-[500] text-(--color-foreground) truncate">
													{profile.appName}
												</span>
											</div>
										</TableCell>
										<TableCell className="px-4 text-sm text-(--color-foreground)">
											{profile.versionName} ({profile.versionCode})
										</TableCell>
										<TableCell className="px-4">
											<Badge variant={profile.isActive ? 'success' : 'muted'}>
												{profile.isActive ? 'Активен' : 'Отключён'}
											</Badge>
										</TableCell>
										<TableCell className="px-4 text-sm text-(--color-muted-foreground)">
											{formatDate(profile.updatedAt)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</div>
			)}
		</>
	)
}
